import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import PaymentModal from "../components/PaymentModal";
import PremiumSuccess from "../components/PremiumSuccess";
import SupportPaymentModal from "../components/SupportPaymentModal";
import CustomAmountModal from "../components/CustomAmountModal";
import toast from "react-hot-toast";
import { api } from "../lib/api/client";
import type { ProductType } from "../hooks/useStripeCheckout";

interface PremiumProduct {
  _id: string;
  name: string;
  description: string;
  imageUrl: string;
  price: number;
  currency: string;
  productType: ProductType;
  isActive: boolean;
}

interface UserPremiumItems {
  premiumFrames: string[];
  premiumBadges: string[];
  premiumBackgrounds: string[];
  activeFrame: string | null;
  activeBadge: string | null;
  activeBackground: string | null;
}

export default function PremiumShop() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [frames, setFrames] = useState<PremiumProduct[]>([]);
  const [badges, setBadges] = useState<PremiumProduct[]>([]);
  const [backgrounds, setBackgrounds] = useState<PremiumProduct[]>([]);
  const [userPremiumItems, setUserPremiumItems] = useState<UserPremiumItems | null>(null);
  
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PremiumProduct | null>(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  
  // Support donation states
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [selectedSupport, setSelectedSupport] = useState<{ amount: number; tier: string } | null>(null);
  const [customAmountModalOpen, setCustomAmountModalOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.error("Premium mağazaya erişmek için giriş yapmalısınız");
      navigate("/login");
      return;
    }

    fetchProducts();
    fetchOwnedItems();
  }, [user, navigate]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Tüm premium ürünleri paralel olarak çek
      const [framesRes, badgesRes, backgroundsRes] = await Promise.all([
        api.get<{ ok: boolean; data: any[] }>("/api/premium/frames"),
        api.get<{ ok: boolean; data: any[] }>("/api/premium/badges"),
        api.get<{ ok: boolean; data: any[] }>("/api/premium/backgrounds"),
      ]);

      if (framesRes.ok && framesRes.data) {
        setFrames(
          framesRes.data
            .filter((f: any) => f.isActive)
            .map((f: any) => ({
              _id: f._id,
              name: f.name,
              description: f.description,
              imageUrl: f.imageUrl,
              price: f.price,
              currency: "TRY",
              productType: "frame" as ProductType,
              isActive: f.isActive,
            }))
        );
      }

      if (badgesRes.ok && badgesRes.data) {
        setBadges(
          badgesRes.data
            .filter((b: any) => b.isActive)
            .map((b: any) => ({
              _id: b._id,
              name: b.name,
              description: b.description,
              imageUrl: b.imageUrl,
              price: b.price,
              currency: "TRY",
              productType: "badge" as ProductType,
              isActive: b.isActive,
            }))
        );
      }

      if (backgroundsRes.ok && backgroundsRes.data) {
        setBackgrounds(
          backgroundsRes.data
            .filter((bg: any) => bg.isActive)
            .map((bg: any) => ({
              _id: bg._id,
              name: bg.name,
              description: bg.description,
              imageUrl: bg.imageUrl,
              price: bg.price,
              currency: "TRY",
              productType: "background" as ProductType,
              isActive: bg.isActive,
            }))
        );
      }
    } catch (err: any) {
      toast.error("Ürünler yüklenirken hata oluştu");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnedItems = async () => {
    if (!user) return;

    try {
      // Kullanıcının sahip olduğu premium itemları çek
      const res = await api.get<{ ok: boolean; data: UserPremiumItems }>(
        "/api/users/premium-items"
      );

      if (res.ok && res.data) {
        setUserPremiumItems(res.data);
      }
    } catch (err) {
      console.error("Owned items fetch error:", err);
    }
  };

  const handleBuyClick = (product: PremiumProduct) => {
    if (!user) {
      toast.error("Satın almak için giriş yapmalısınız");
      navigate("/login");
      return;
    }

    // Check if user owns this product
    const isOwned = isProductOwned(product);
    if (isOwned) {
      toast.error("Bu ürüne zaten sahipsiniz");
      return;
    }

    setSelectedProduct(product);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentModalOpen(false);
    setSuccessModalOpen(true);
    
    // Owned items'ı backend'den tekrar çek
    fetchOwnedItems();

    // 5 saniye sonra success modal'ı kapat
    setTimeout(() => {
      setSuccessModalOpen(false);
    }, 5000);
  };

  const handleSupportClick = (amount: number, tier: string) => {
    if (!user) {
      toast.error("Destek olmak için giriş yapmalısınız");
      navigate("/login");
      return;
    }

    setSelectedSupport({ amount, tier });
    setSupportModalOpen(true);
  };

  const handleCustomAmountClick = () => {
    if (!user) {
      toast.error("Destek olmak için giriş yapmalısınız");
      navigate("/login");
      return;
    }

    setCustomAmountModalOpen(true);
  };

  const handleCustomAmountSubmit = (amount: number) => {
    setCustomAmountModalOpen(false);
    setSelectedSupport({ amount, tier: "Özel Destek" });
    setSupportModalOpen(true);
  };

  const isProductOwned = (product: PremiumProduct): boolean => {
    if (!userPremiumItems) return false;

    switch (product.productType) {
      case "frame":
        return userPremiumItems.premiumFrames.includes(product._id);
      case "badge":
        return userPremiumItems.premiumBadges.includes(product._id);
      case "background":
        return userPremiumItems.premiumBackgrounds.includes(product._id);
      default:
        return false;
    }
  };

  const renderProductCard = (product: PremiumProduct) => {
    const isOwned = isProductOwned(product);

    return (
      <div
        key={product._id}
        style={{
          background: "var(--card-bg, #1a1a1a)",
          border: isOwned ? "2px solid var(--primary, #FFD700)" : "1px solid var(--muted, #333)",
          borderRadius: 12,
          overflow: "hidden",
          transition: "all 0.3s ease",
          cursor: "pointer",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          if (!isOwned) {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.3)";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        {/* Owned Badge */}
        {isOwned && (
          <div
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              background: "var(--primary, #FFD700)",
              color: "#000",
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700,
              zIndex: 1,
            }}
          >
            ✓ Sahip Olunan
          </div>
        )}

        {/* Product Image */}
        <div
          style={{
            width: "100%",
            height: 200,
            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <img
            src={product.imageUrl}
            alt={product.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "/placeholder-premium.png";
            }}
          />
        </div>

        {/* Product Info */}
        <div style={{ padding: 16 }}>
          <h3
            style={{
              margin: 0,
              marginBottom: 8,
              fontSize: 18,
              fontWeight: 600,
              color: "var(--text, #fff)",
            }}
          >
            {product.name}
          </h3>
          <p
            style={{
              margin: 0,
              marginBottom: 16,
              fontSize: 14,
              color: "var(--muted-text, #aaa)",
              lineHeight: 1.5,
              minHeight: 42,
            }}
          >
            {product.description}
          </p>

          {/* Price & Button */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div>
              <span
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "var(--primary, #FFD700)",
                }}
              >
                {product.price} {product.currency}
              </span>
            </div>
            <button
              onClick={() => handleBuyClick(product)}
              disabled={isOwned}
              style={{
                padding: "10px 20px",
                borderRadius: 8,
                border: "none",
                background: isOwned
                  ? "var(--muted, #333)"
                  : "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                color: isOwned ? "#666" : "#000",
                cursor: isOwned ? "not-allowed" : "pointer",
                fontSize: 14,
                fontWeight: 600,
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!isOwned) {
                  e.currentTarget.style.transform = "scale(1.05)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              {isOwned ? "Sahipsin" : "Satın Al"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 50,
              height: 50,
              border: "4px solid var(--muted, #333)",
              borderTopColor: "var(--primary, #FFD700)",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p style={{ color: "var(--muted-text, #aaa)" }}>Yükleniyor...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 16px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 40, textAlign: "center" }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 12,
            fontSize: 36,
            fontWeight: 700,
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Premium Mağaza
        </h1>
        <p style={{ margin: 0, fontSize: 16, color: "var(--muted-text, #aaa)" }}>
          Profilini özelleştir, öne çık! 🌟
        </p>
      </div>

      {/* Frames Section */}
      {frames.length > 0 && (
        <section style={{ marginBottom: 48 }}>
          <h2
            style={{
              margin: 0,
              marginBottom: 24,
              fontSize: 24,
              fontWeight: 600,
              color: "var(--text, #fff)",
            }}
          >
            🖼️ Çerçeveler
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 24,
            }}
          >
            {frames.map(renderProductCard)}
          </div>
        </section>
      )}

      {/* Badges Section */}
      {badges.length > 0 && (
        <section style={{ marginBottom: 48 }}>
          <h2
            style={{
              margin: 0,
              marginBottom: 24,
              fontSize: 24,
              fontWeight: 600,
              color: "var(--text, #fff)",
            }}
          >
            🏅 Rozetler
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 24,
            }}
          >
            {badges.map(renderProductCard)}
          </div>
        </section>
      )}

      {/* Backgrounds Section */}
      {backgrounds.length > 0 && (
        <section style={{ marginBottom: 48 }}>
          <h2
            style={{
              margin: 0,
              marginBottom: 24,
              fontSize: 24,
              fontWeight: 600,
              color: "var(--text, #fff)",
            }}
          >
            🎨 Arka Planlar
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 24,
            }}
          >
            {backgrounds.map(renderProductCard)}
          </div>
        </section>
      )}

      {/* Support Section */}
      <section style={{ marginBottom: 48, marginTop: 60 }}>
        <div style={{ 
          background: "linear-gradient(135deg, rgba(17, 66, 50, 0.2) 0%, rgba(17, 66, 50, 0.05) 100%)",
          border: "2px solid rgba(17, 66, 50, 0.3)",
          borderRadius: 16,
          padding: 40,
          textAlign: "center"
        }}>
          <h2 style={{
            margin: 0,
            marginBottom: 12,
            fontSize: 28,
            fontWeight: 700,
            color: "#114232"
          }}>
            💚 Site Gelişim için Destek Ol
          </h2>
          <p style={{
            margin: 0,
            marginBottom: 32,
            fontSize: 16,
            color: "var(--muted-text, #aaa)",
            maxWidth: 600,
            marginLeft: "auto",
            marginRight: "auto"
          }}>
            SavNot'un gelişmesine ve devamlılığına katkıda bulunmak isterseniz, 
            aşağıdaki destek paketlerinden birini seçebilirsiniz. Her türlü destek çok değerlidir! 🙏
          </p>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 20,
            maxWidth: 1000,
            margin: "0 auto"
          }}>
            {/* 50 TL Support */}
            <button
              onClick={() => handleSupportClick(50, "Destek")}
              style={{
                background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                border: "2px solid #114232",
                borderRadius: 12,
                padding: "24px 20px",
                cursor: "pointer",
                transition: "all 0.3s",
                position: "relative",
                overflow: "hidden"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(17, 66, 50, 0.4)";
                e.currentTarget.style.borderColor = "#1a6b4f";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#114232";
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌱</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
                50 TL
              </div>
              <div style={{ fontSize: 14, color: "#114232", fontWeight: 600 }}>
                Destek Ol
              </div>
            </button>

            {/* 250 TL Support */}
            <button
              onClick={() => handleSupportClick(250, "Destekçi")}
              style={{
                background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                border: "2px solid #114232",
                borderRadius: 12,
                padding: "24px 20px",
                cursor: "pointer",
                transition: "all 0.3s",
                position: "relative",
                overflow: "hidden"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(17, 66, 50, 0.4)";
                e.currentTarget.style.borderColor = "#1a6b4f";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#114232";
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌿</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
                250 TL
              </div>
              <div style={{ fontSize: 14, color: "#114232", fontWeight: 600 }}>
                Destekçi
              </div>
            </button>

            {/* 1000 TL Support */}
            <button
              onClick={() => handleSupportClick(1000, "Gelişim Destekçisi")}
              style={{
                background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
                border: "2px solid #114232",
                borderRadius: 12,
                padding: "24px 20px",
                cursor: "pointer",
                transition: "all 0.3s",
                position: "relative",
                overflow: "hidden"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(17, 66, 50, 0.4)";
                e.currentTarget.style.borderColor = "#1a6b4f";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#114232";
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>🌳</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
                1000 TL
              </div>
              <div style={{ fontSize: 14, color: "#114232", fontWeight: 600 }}>
                Gelişim Destekçisi
              </div>
            </button>

            {/* 5000 TL Support */}
            <button
              onClick={() => handleSupportClick(5000, "Efsane Destekçi")}
              style={{
                background: "linear-gradient(135deg, #114232 0%, #1a6b4f 100%)",
                border: "2px solid #FFD700",
                borderRadius: 12,
                padding: "24px 20px",
                cursor: "pointer",
                transition: "all 0.3s",
                position: "relative",
                overflow: "hidden"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(255, 215, 0, 0.4)";
                e.currentTarget.style.borderColor = "#FFA500";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#FFD700";
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>👑</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#FFD700", marginBottom: 4 }}>
                5000 TL
              </div>
              <div style={{ fontSize: 14, color: "#FFD700", fontWeight: 600 }}>
                Efsane Destekçi
              </div>
            </button>

            {/* Custom Amount Button */}
            <button
              onClick={handleCustomAmountClick}
              style={{
                background: "#1a1a1a",
                border: "2px dashed #114232",
                borderRadius: 12,
                padding: "24px 20px",
                cursor: "pointer",
                transition: "all 0.3s",
                position: "relative",
                overflow: "hidden"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(17, 66, 50, 0.4)";
                e.currentTarget.style.borderColor = "#1a6b4f";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#114232";
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>💰</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", marginBottom: 4 }}>
                Sen Belirle
              </div>
              <div style={{ fontSize: 14, color: "#114232", fontWeight: 600 }}>
                İstediğin Tutarı Gir
              </div>
            </button>
          </div>

          <p style={{
            marginTop: 32,
            marginBottom: 0,
            fontSize: 13,
            color: "var(--muted-text, #888)",
            fontStyle: "italic"
          }}>
            ℹ️ Destek ödemesi için kredi kartı veya banka havalesi kullanabilirsiniz
          </p>
        </div>
      </section>

      {/* Empty State */}
      {!loading && frames.length === 0 && badges.length === 0 && backgrounds.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 60,
            color: "var(--muted-text, #aaa)",
          }}
        >
          <p style={{ fontSize: 18, marginBottom: 8 }}>Henüz premium ürün bulunmuyor</p>
          <p style={{ fontSize: 14 }}>Yakında yeni ürünler eklenecek! 🎁</p>
        </div>
      )}

      {/* Payment Modal */}
      {selectedProduct && (
        <PaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          productType={selectedProduct.productType}
          productId={selectedProduct._id}
          productName={selectedProduct.name}
          productPrice={selectedProduct.price}
          productCurrency={selectedProduct.currency}
          productImage={selectedProduct.imageUrl}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Success Modal */}
      <PremiumSuccess
        show={successModalOpen}
        productName={selectedProduct?.name || "Premium Ürün"}
        productImage={selectedProduct?.imageUrl}
      />

      {/* Support Payment Modal */}
      {selectedSupport && (
        <SupportPaymentModal
          isOpen={supportModalOpen}
          onClose={() => setSupportModalOpen(false)}
          amount={selectedSupport.amount}
          tier={selectedSupport.tier}
          onSuccess={() => {
            setSupportModalOpen(false);
            toast.success("Desteğiniz için teşekkür ederiz! 💚");
          }}
        />
      )}

      {/* Custom Amount Modal */}
      <CustomAmountModal
        isOpen={customAmountModalOpen}
        onClose={() => setCustomAmountModalOpen(false)}
        onSubmit={handleCustomAmountSubmit}
      />
    </div>
  );
}
